import { Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Client-side role guard. Backend already enforces 403 for cross-role access,
 * but we redirect + notify so that a non-authorized user does not see empty
 * shells with 403 fetch noise. Accepts a single `role` string or an array.
 *
 * Usage:
 *   <RequireRole role="ADMIN"><Admin /></RequireRole>
 *   <RequireRole role={["ADMIN","SUPERVISOR"]}><Pesaxes /></RequireRole>
 */
export default function RequireRole({ role, children, fallback = "/app/dashboard" }) {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const notified = useRef(false);
  const allowed = Array.isArray(role) ? role : [role];
  const ok = !!user && allowed.includes(user.role);

  useEffect(() => {
    if (!loading && user && !ok && !notified.current) {
      notified.current = true;
      toast.error(t("auth.no_permission"), {
        description: t("auth.no_permission_sub"),
        duration: 4000,
      });
    }
  }, [loading, user, ok, t]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!ok) return <Navigate to={fallback} replace data-testid="role-guard-redirect" />;
  return children;
}
